import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useAgencyAvailability } from "../../hooks/useAgencyAvailability";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { FONT_SIZE } from "../../theme/tokens";
import { agencySourceLabel } from "../../utils/agencyIntegrations";
import { getDepartmentPostDelayHours, MIN_AGENCY_POST_DELAY_HOURS, parseDelayHours } from "../../utils/agencyPostDelay";
import { getAgencySubRoleInfo } from "../../utils/agencySubRoles";
import type { AgencySource, NurseRole } from "../../utils/models";
import { nurseRoleDisplayName } from "../../utils/utilFunctions";
import { useGetDepartmentSubRoles } from "../staffingSearch/useGetDepartmentSubRoles";
import { DelayHoursField } from "../UIToolkit/Inputs/DelayHoursField";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import SystemDialog from "../UIToolkit/Popups/Dialog";

/** The resolved agency-post selection the host should send to `postRequestToAgency`. */
export type AgencyPostConfirmation = {
	/** The chosen agency, as the backend `agency_source` array expects (lowercase enum values). */
	agencySource: AgencySource[];
	/** The per-(job, agency) sub-role pick; omitted when the catalog has no sub-roles. */
	subRole?: NurseRole;
	/** Whether to hold the post back until closer to the shift. */
	postWithDelay: boolean;
	/** Hours to wait; only meaningful when `postWithDelay` is set. */
	agencyPostDelayHours?: number;
};

type PostToAgencyDialogProps = {
	open: boolean;
	/**
	 * The job this request targets (job-management departments). Sub-role options are looked up per
	 * (job, agency) in that mode.
	 */
	jobId?: string;
	/** The request's role groups — used to resolve sub-role options in role-group (non-job-management) mode. */
	roleGroups: string[];
	/** Whether job management is enabled — selects between job-based and role-group sub-role lookup. */
	jobManagementEnabled: boolean;
	onClose: () => void;
	/** Fires with the resolved selection when the admin confirms. */
	onConfirm: (confirmation: AgencyPostConfirmation) => void;
	confirmLoading?: boolean;
};

/**
 * Confirm dialog for posting an open shift to an agency. Single available agency → disabled select;
 * multiple → enabled picker. Reuses the create-flow sub-role catalog (`useGetDepartmentSubRoles`).
 */
export const PostToAgencyDialog: FC<PostToAgencyDialogProps> = ({ open, jobId, roleGroups, jobManagementEnabled, onClose, onConfirm, confirmLoading }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { availableAgencies } = useAgencyAvailability();
	const { data: subRoleCatalog = [] } = useGetDepartmentSubRoles(chosenDepartment.department_id);
	const delayedAgencyPostingEnabled = useFeatureFlag(FEATURE_FLAGS.DelayedAgencyPostingEnabled);
	const [postWithDelay, setPostWithDelay] = useState<boolean>(true);
	const [delayHours, setDelayHours] = useState<string>(String(getDepartmentPostDelayHours(chosenDepartment)));

	const initialAgency = availableAgencies[0] ?? null;
	const [selectedAgency, setSelectedAgency] = useState<AgencySource | null>(initialAgency);
	// Initialize from the first available agency's default so the confirm button doesn't flicker
	// disabled→enabled while a default is applied post-mount. The effect below handles later changes.
	const [subRole, setSubRole] = useState<NurseRole | undefined>(
		() =>
			getAgencySubRoleInfo({
				catalog: subRoleCatalog,
				agencySource: initialAgency,
				jobManagementEnabled,
				roleGroups: chosenDepartment.role_groups,
				row: { jobId, roleGroups },
			}).defaultValue,
	);

	// Reset to the first available agency on open.
	useEffect(() => {
		if (!open) return;
		setSelectedAgency((prev) => (prev && availableAgencies.includes(prev) ? prev : (availableAgencies[0] ?? null)));
	}, [open, availableAgencies]);

	// Sub-role options + default (mirrors the create flow: job-based when job management is on,
	// role-group based otherwise).
	const subRoleInfo = useMemo<{ options: NurseRole[]; defaultValue: NurseRole | undefined }>(
		() =>
			getAgencySubRoleInfo({
				catalog: subRoleCatalog,
				agencySource: selectedAgency,
				jobManagementEnabled,
				roleGroups: chosenDepartment.role_groups,
				row: { jobId, roleGroups },
			}),
		[jobId, roleGroups, jobManagementEnabled, selectedAgency, subRoleCatalog, chosenDepartment.role_groups],
	);

	// Default the sub-role when the current pick isn't in the new options (options/agency changed after mount).
	useEffect(() => {
		if (subRoleInfo.options.length === 0) {
			setSubRole(undefined);
			return;
		}
		setSubRole((prev) => (prev && subRoleInfo.options.includes(prev) ? prev : subRoleInfo.defaultValue));
	}, [subRoleInfo]);

	const hasMultiple = availableAgencies.length > 1;
	const agencyLabel = selectedAgency ? agencySourceLabel(selectedAgency) : "";

	// Only relevant when the delay is actually being applied; otherwise the typed value is ignored.
	const delayApplies = delayedAgencyPostingEnabled && postWithDelay;
	const parsedDelayHours = parseDelayHours(delayHours);
	const delayInvalid = delayApplies && parsedDelayHours === null;

	const handleConfirm = () => {
		if (!selectedAgency || delayInvalid) return;
		onConfirm({
			agencySource: [selectedAgency],
			subRole,
			postWithDelay: delayApplies,
			agencyPostDelayHours: delayApplies ? (parsedDelayHours ?? undefined) : undefined,
		});
	};

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1, minWidth: { xs: "auto", sm: 320 } }}>
			<Typography sx={{ color: (theme) => alpha(theme.palette.primary.dark, 0.7), fontSize: FONT_SIZE.TITLE_SM }}>
				{t("post.to.agency.confirm.body", { agency: agencyLabel })}
			</Typography>
			<SystemDropdown<AgencySource>
				data-testid="post-to-agency-dialog-agency"
				label={t("agency")}
				items={availableAgencies.map((source) => ({ value: source, label: agencySourceLabel(source), selectedLabel: agencySourceLabel(source) }))}
				selectedItem={selectedAgency ?? undefined}
				onChange={(value) => setSelectedAgency(value ?? null)}
				disabled={!hasMultiple}
				fullWidth
			/>
			{subRoleInfo.options.length > 0 && (
				<SystemDropdown<NurseRole>
					data-testid="post-to-agency-dialog-sub-role"
					label={t("sub.role")}
					items={subRoleInfo.options.map((r) => ({ value: r, label: nurseRoleDisplayName[r] ?? r }))}
					selectedItem={subRole}
					onChange={(value) => setSubRole(value)}
					fullWidth
				/>
			)}
			{delayedAgencyPostingEnabled && (
				<DelayHoursField
					checked={postWithDelay}
					onCheckedChange={setPostWithDelay}
					checkboxLabel={t("delay")}
					value={delayHours}
					onChange={setDelayHours}
					suffixLabel={t("hours.before.shift.start")}
					invalidHelperText={t("agency.post.delay.invalid", { min: MIN_AGENCY_POST_DELAY_HOURS })}
					minHours={MIN_AGENCY_POST_DELAY_HOURS}
					dataTestId="post-to-agency-dialog-delay"
				/>
			)}
		</Box>
	);

	return (
		<SystemDialog
			dataTestId="post-to-agency-dialog"
			open={open}
			onClose={onClose}
			title={t("post.to.agency.confirm.title", { agency: agencyLabel })}
			body={body}
			confirm={t("post.to.agency")}
			onConfirm={handleConfirm}
			confirmDisabled={!selectedAgency || (subRoleInfo.options.length > 0 && !subRole) || delayInvalid}
			confirmLoading={confirmLoading}
		/>
	);
};
